// Long-task monitor: catches main-thread blocks (>= LONG_TASK_THRESHOLD_MS)
// and reports them to Sentry with route, recordId and memory snapshot.
// Cheap (PerformanceObserver buffers in browser), prod-safe, dev-noisy.

import { addBreadcrumb, captureMessage } from './sentry';

// Browser blocks shorter than this are normal (animations, GC, paint).
// 200ms is the threshold where users start to feel jank; 1s is a clear freeze.
const LONG_TASK_THRESHOLD_MS = 200;
const FREEZE_THRESHOLD_MS = 1000;

// Throttle to avoid flooding Sentry during sustained jank.
const REPORT_INTERVAL_MS = 5000;

interface MemorySnapshot {
  usedJSHeapSizeMB?: number;
  totalJSHeapSizeMB?: number;
  jsHeapSizeLimitMB?: number;
}

function readMemory(): MemorySnapshot {
  // performance.memory is Chromium-only and non-standard, but our prod traffic is mostly Chromium.
  const perf = performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  if (!perf.memory) return {};
  const toMB = (bytes: number) => Math.round(bytes / 1024 / 1024);
  return {
    usedJSHeapSizeMB: toMB(perf.memory.usedJSHeapSize),
    totalJSHeapSizeMB: toMB(perf.memory.totalJSHeapSize),
    jsHeapSizeLimitMB: toMB(perf.memory.jsHeapSizeLimit),
  };
}

function extractRecordId(): string | null {
  // URL shapes: /records/<id>, /embedded/records/<id>, /records?id=<id>
  // (the records page keeps the selected record in the query string), and
  // /records?recordId=<id> — the alias produced by notification actionUrls
  // and dashboard deep links until RecordsPage rewrites it to ?id=.
  const match = window.location.pathname.match(/\/records\/([^/?#]+)/);
  if (match?.[1]) return match[1];
  const params = new URLSearchParams(window.location.search);
  return params.get('id') ?? params.get('recordId');
}

// Long Tasks API attribution: coarse (often just "window"), but when a task
// comes from an iframe or named container it points straight at the culprit.
function readAttribution(entry: PerformanceEntry): Record<string, string> {
  const attribution = (
    entry as PerformanceEntry & {
      attribution?: Array<{
        name?: string;
        containerType?: string;
        containerName?: string;
        containerSrc?: string;
      }>;
    }
  ).attribution?.[0];
  if (!attribution) return {};
  const out: Record<string, string> = {};
  if (attribution.name) out.taskName = attribution.name;
  if (attribution.containerType) out.containerType = attribution.containerType;
  if (attribution.containerName) out.containerName = attribution.containerName;
  if (attribution.containerSrc) out.containerSrc = attribution.containerSrc;
  return out;
}

// Was the user interacting, or did the block fire on an idle tab? This
// distinguishes "heavy render on click" from "background work storm" — the
// two have completely different fixes. Timestamps share performance.now()'s
// origin with entry.startTime.
let lastInteractionTs = 0;
let lastVisibilityChangeTs = 0;

// IDs (UUIDs, rec_/prefixed ids, numerics) collapse to ':id' so the Sentry
// tag and message have bounded cardinality — raw /records/<uuid> paths would
// create one tag value (and one issue group) per record.
//
// The normalized route is now also what gets PERSISTED (both to Sentry
// `extra` below and to the localStorage sentinel in `beat()`) — not the raw
// pathname. A route like `/invitations/<token>` carries a live 7-day org
// invite as its ":id" segment (apps/edge-api/src/routes/users/core.ts:345);
// "exact triage" is not worth shipping a bearer credential to a third party
// or parking it in browser storage. See
// docs/audit/2026-07-31-egress-ui-worker-sentry.md §5.3.
const UUID_SEGMENT = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
// One OR MORE dash-joined UUIDs. The invitation token is
// `crypto.randomUUID() + '-' + crypto.randomUUID()` — two UUIDs joined by a
// dash, 73 characters. A pattern anchored to exactly one UUID silently
// misses it: the previous regex here did exactly that, which is why this
// function failed to strip the token out of `route`/`tags.pathname` despite
// being the mechanism that was supposed to do so.
const UUID_CHAIN_RE = new RegExp(`^(?:${UUID_SEGMENT})(?:-(?:${UUID_SEGMENT}))*$`, 'i');
const TYPED_ID_RE = /^[a-z]+_[a-zA-Z0-9]+$/;
const NUMERIC_ID_RE = /^\d+$/;
// Defense in depth against the NEXT secret shape, not just this one: a
// denylist of known ID *shapes* fails open on any shape it wasn't written
// for (the exact bug above, and the same structural pattern the parent
// remediation program flags for path masking generally — see
// docs/plans/2026-07-31-egress-remediation.md §1). Any segment this long,
// made only of id-ish characters, and containing at least one digit is
// treated as opaque and collapsed too, even without a named shape match. No
// static route segment in this app's `createFileRoute` calls is this long
// or contains a digit (checked against every route at the time of writing),
// so this cannot misfire on a real route name — worst case it collapses a
// future opaque id/secret that isn't UUID-shaped.
const OPAQUE_SEGMENT_RE = /^[0-9a-zA-Z_-]{20,}$/;

function isIdSegment(segment: string): boolean {
  if (UUID_CHAIN_RE.test(segment)) return true;
  if (TYPED_ID_RE.test(segment)) return true;
  if (NUMERIC_ID_RE.test(segment)) return true;
  return OPAQUE_SEGMENT_RE.test(segment) && /\d/.test(segment);
}

export function normalizePathname(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => (isIdSegment(segment) ? ':id' : segment))
    .join('/');
}

let lastReportTs = 0;

function handleEntry(entry: PerformanceEntry) {
  if (entry.duration < LONG_TASK_THRESHOLD_MS) return;

  const isFreeze = entry.duration >= FREEZE_THRESHOLD_MS;

  // Always drop a breadcrumb — cheap, gives Sentry context for the next error.
  addBreadcrumb(
    `Long task ${Math.round(entry.duration)}ms`,
    'performance',
    isFreeze ? 'warning' : 'info'
  );

  // Throttle full reports. Payload work (memory read, URL parsing) happens
  // only below this gate — during sustained jank entries arrive continuously
  // and the main thread is already saturated.
  const now = Date.now();
  if (now - lastReportTs < REPORT_INTERVAL_MS) return;
  lastReportTs = now;

  // Normalized once, up front: `payload.pathname` below carries this same
  // normalized value, not the raw pathname — a raw `/invitations/<token>`
  // must not ride into `extra` (still sent to Sentry) or into the
  // localStorage sentinel (see `beat()`), even though this is only the
  // long-task report, not the terminal-freeze sentinel.
  const route = normalizePathname(window.location.pathname);
  const payload = {
    durationMs: Math.round(entry.duration),
    startTime: Math.round(entry.startTime),
    pathname: route,
    recordId: extractRecordId(),
    visibilityState: document.visibilityState,
    msSinceInteraction: lastInteractionTs
      ? Math.round(entry.startTime - lastInteractionTs)
      : null,
    msSinceVisibilityChange: lastVisibilityChangeTs
      ? Math.round(entry.startTime - lastVisibilityChangeTs)
      : null,
    ...readAttribution(entry),
    ...readMemory(),
  };

  if (isFreeze) {
    // Use captureMessage so this surfaces as a Sentry issue, not just a breadcrumb.
    // Memory snapshot + recordId ride along as extras — a heap near its limit
    // points at allocation/GC storms rather than slow renders.
    // Message, tag AND extra all use the NORMALIZED path (bounded
    // cardinality, one issue group per route, and no secret in any of the
    // three); exact duration lives in extras alongside it.
    captureMessage(`UI freeze on ${route}`, 'warning', {
      tags: { pathname: route },
      extra: payload,
    });
  }

  // Dev: surface in console so we see it locally without opening Sentry.
  if (import.meta.env.DEV) {
    console.warn('[freeze-monitor]', payload);
  }
}

// --- Terminal freeze sentinel ----------------------------------------------
// PerformanceObserver fires only AFTER a long task completes — a terminal
// freeze (tab killed or force-reloaded mid-block) never reports and is
// invisible in Sentry. Instead every tab heartbeats into localStorage; on app
// boot, any tab entry that went silent WITHOUT a clean pagehide is reported
// as a suspected terminal freeze, carrying the context of its last beat.

const SENTINEL_PREFIX = 'aica:freeze-sentinel:';
// The tab's own sentinel key, persisted in sessionStorage: it survives
// reload and Memory Saver discard (same tab), so a reborn tab can recognize
// and retract its predecessor's entry when document.wasDiscarded is set.
const SENTINEL_KEY_SLOT = 'aica:freeze-sentinel-key';
const HEARTBEAT_INTERVAL_MS = 5_000;
// Chrome throttles background-tab timers down to one run per minute; 3 missed
// minutes without a beat means the tab is dead, not just backgrounded.
const SENTINEL_STALE_AFTER_MS = 180_000;
// After a whole-browser crash every tab leaves a dirty sentinel — report a
// few, swallow the rest.
const MAX_SENTINEL_REPORTS_PER_BOOT = 3;

interface SentinelRecord extends MemorySnapshot {
  ts: number; // epoch ms of last heartbeat
  startedAt: number; // epoch ms of session start
  pathname: string;
  recordId: string | null;
  clean?: boolean; // pagehide/freeze ran — close, navigation, or browser-initiated suspension
}

// A value that parses but has the wrong shape (schema drift between deploys,
// partial write) must be treated as corrupt, not fed to the report path —
// `now - undefined` is NaN and would classify garbage as a stale death.
function parseSentinel(raw: string | null): SentinelRecord | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as { ts?: unknown }).ts === 'number' &&
      typeof (parsed as { pathname?: unknown }).pathname === 'string'
    ) {
      return parsed as SentinelRecord;
    }
  } catch {
    // Unparseable — fall through to null.
  }
  return null;
}

// Must run AFTER Sentry init resolves (see main.tsx): captureMessage falls
// back to console when Sentry isn't ready, and the sentinel is deleted here —
// a pre-init sweep would silently destroy every terminal-freeze report.
export function sweepDeadSentinels(): void {
  const now = Date.now();
  let reported = 0;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(SENTINEL_PREFIX)) continue;
    const record = parseSentinel(localStorage.getItem(key));
    if (record && !record.clean && now - record.ts < SENTINEL_STALE_AFTER_MS) {
      continue; // a live tab elsewhere (or a recent death — picked up once stale)
    }
    localStorage.removeItem(key);
    if (!record || record.clean) continue;
    if (reported >= MAX_SENTINEL_REPORTS_PER_BOOT) continue;
    reported++;
    const route = normalizePathname(record.pathname);
    captureMessage(`Suspected terminal freeze on ${route}`, 'error', {
      tags: { pathname: route },
      extra: {
        ...record,
        lastBeatAt: new Date(record.ts).toISOString(),
        sessionDurationMs: record.ts - record.startedAt,
        msSinceLastBeat: now - record.ts,
      },
    });
  }
}

export function startSentinel(): void {
  // Memory Saver discard reloads the tab with document.wasDiscarded set and
  // never fires pagehide — the predecessor's dirty sentinel is browser-managed
  // suspension, not a freeze. Retract it so the next sweep stays quiet.
  // (A force-reload of a genuinely frozen tab has wasDiscarded unset, so real
  // terminal freezes in the same tab still report.)
  try {
    const prevKey = sessionStorage.getItem(SENTINEL_KEY_SLOT);
    if (prevKey && (document as Document & { wasDiscarded?: boolean }).wasDiscarded) {
      localStorage.removeItem(prevKey);
    }
  } catch {
    // sessionStorage unavailable — skip the retraction.
  }

  const key = SENTINEL_PREFIX + crypto.randomUUID();
  const startedAt = Date.now();
  // During unload the spec fires pagehide BEFORE visibilitychange(hidden);
  // without this latch the trailing visibilitychange beat would overwrite the
  // clean marker and every normal close of a visible tab would later report
  // as a false terminal freeze.
  let closed = false;
  const beat = (clean?: boolean) => {
    try {
      const record: SentinelRecord = {
        ts: Date.now(),
        startedAt,
        // Normalized, not raw: this record is written to localStorage on
        // every heartbeat (every 5s while the tab is open) and later spread
        // into a Sentry `extra` payload by `sweepDeadSentinels` — a raw
        // `/invitations/<token>` pathname would sit in both places for as
        // long as the sentinel lives. See docs/audit/
        // 2026-07-31-egress-ui-worker-sentry.md §5.3.
        pathname: normalizePathname(window.location.pathname),
        recordId: extractRecordId(),
        ...readMemory(),
        ...(clean ? { clean } : {}),
      };
      localStorage.setItem(key, JSON.stringify(record));
      sessionStorage.setItem(SENTINEL_KEY_SLOT, key);
    } catch {
      // localStorage unavailable (private mode, quota) — sentinel disabled.
    }
  };
  beat();
  setInterval(() => {
    if (!closed) beat();
  }, HEARTBEAT_INTERVAL_MS);
  // Capture the freshest state when backgrounding (background beats throttle).
  // Only the hidden edge matters — the visible edge is covered by the interval.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && !closed) beat();
  });
  // Normal unload marks the sentinel clean — the next boot sweeps it silently.
  // A tab killed mid-freeze never gets here: that dirty entry IS the signal.
  window.addEventListener('pagehide', () => {
    closed = true;
    beat(true);
  });
  // bfcache restore: the page is alive again.
  window.addEventListener('pageshow', () => {
    closed = false;
    beat();
  });
  // Page Lifecycle (Chromium): the browser freezes background tabs before
  // discarding them. Suspension is not a crash — mark clean; resume re-dirties.
  document.addEventListener('freeze', () => beat(true));
  document.addEventListener('resume', () => {
    closed = false;
    beat();
  });
}

export function initFreezeMonitor(): void {
  if (typeof window === 'undefined') return;

  // The boot sweep is NOT called here — main.tsx runs it after Sentry init so
  // the reports have somewhere to go.
  try {
    startSentinel();
  } catch {
    // Defensive: never break app boot because monitoring failed.
  }

  if (typeof PerformanceObserver === 'undefined') return;

  // Long Tasks API is supported in Chromium and recent Firefox; Safari ignores it silently.
  const supported = PerformanceObserver.supportedEntryTypes?.includes('longtask');
  if (!supported) return;

  const markInteraction = () => {
    lastInteractionTs = performance.now();
  };
  window.addEventListener('pointerdown', markInteraction, {
    passive: true,
    capture: true,
  });
  window.addEventListener('keydown', markInteraction, { passive: true, capture: true });
  document.addEventListener('visibilitychange', () => {
    lastVisibilityChangeTs = performance.now();
  });

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        handleEntry(entry);
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    // Defensive: never break app boot because monitoring failed.
  }
}
